import type { ReactNode } from "react";
import React, { useState } from "react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { formatIntToCurrency } from "@repo/utils";
import { XIcon } from "lucide-react";

import { cn } from "~/lib/utils";
import { MemoizedKeypadInput } from "../ui/keypad-input";
import { Button } from "./button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./dialog";

type ValueByType<T> = T extends "number" | "decimal"
  ? number
  : T extends "password"
    ? string
    : string;

const INPUT_TYPES = {
  password: "password",
  number: "number",
  decimal: "decimal",
  text: "text",
} as const;

type InputType = keyof typeof INPUT_TYPES;

interface ModalInputProps<T extends InputType> {
  value?: ValueByType<T>;
  onChange: (value: ValueByType<T>) => void;
  onClick?: () => void;
  onClose?: () => void;
  onBlur?: () => void;
  defaultOpen?: boolean;
  description?: string;
  type: T;
  min?: number;
  max?: number;
  maxButton?: boolean;
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
  label?: string;
  icon?: React.ReactNode;
  passwordLength?: number;
  "aria-invalid"?: boolean;
}

export const ModalInput = <T extends InputType>(props: ModalInputProps<T>) => {
  const [dialogOpen, setDialogOpen] = useState(props.defaultOpen ?? false);

  const handleDialog = (open: boolean) => {
    setDialogOpen(open);
    if (open === false) {
      props.onClose?.();
    }
  };
  return (
    <Dialog open={dialogOpen} onOpenChange={handleDialog}>
      <DialogTrigger asChild>
        {props.children ?? (
          <Button
            onClick={props.onClick}
            aria-invalid={props["aria-invalid"]}
            variant="outline"
            disabled={props.disabled}
            className={cn(
              "w-full shadow-none border-border justify-start px-4 py-2 text-lg aria-[invalid=true]:border-red-500",
              props.className,
              props.label && "justify-between",
            )}
          >
            {props.label && (
              <span className="text-foreground">{props.label}</span>
            )}
            {props.type === "decimal" &&
              typeof props.value === "number" &&
              formatIntToCurrency(props.value, false)}
            {props.type === "number" &&
              typeof props.value === "number" &&
              parseInt(props.value.toString())}
            {props.type === "password" && "*".repeat(props.passwordLength ?? 4)}
            {props.type !== "number" &&
              props.type !== "decimal" &&
              props.type !== "password" &&
              props.value}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="p-0">
        <VisuallyHidden>
          <DialogHeader>
            <DialogTitle></DialogTitle>
            <DialogDescription></DialogDescription>
          </DialogHeader>
        </VisuallyHidden>
        <DialogClose asChild>
          <Button
            className="absolute top-2 right-2 p-1 rounded-full w-8 h-8"
            variant="ghost"
          >
            <XIcon />
          </Button>
        </DialogClose>
        <MemoizedKeypadInput
          maxButton={props.maxButton}
          label={props.label}
          description={props.description}
          max={props.max}
          min={props.min}
          icon={props.icon}
          disabled={props.disabled}
          value={props.value}
          onBlur={props.onBlur}
          onChange={(value) => {
            props.onChange(value);
            setDialogOpen(false);
          }}
          type={props.type}
        />
      </DialogContent>
    </Dialog>
  );
};
